import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import AppShell from "../components/app/AppShell";
import { useAuth } from "../contexts/AuthContext";
import { useT } from "../contexts/LanguageContext";
import { api } from "../lib/api";
import { usePlans } from "../lib/usePlans";
import { toast } from "sonner";
import { CheckCircle, CreditCard, ShieldCheck, X } from "@phosphor-icons/react";

const PLANS_STATIC_FALLBACK = [
  { id: "free",      price_try: 0,    period_key: "billing.period_trial", featured: false },
  { id: "monthly",   price_try: 1250, period_key: "billing.period_month", featured: false },
  { id: "quarterly", price_try: 3000, period_key: "billing.period_3month", featured: true  },
  { id: "semi",      price_try: 5000, period_key: "billing.period_6month", featured: false },
];

export default function Profile() {
  const { user, refresh } = useAuth();
  const { t, lang } = useT();
  const location = useLocation();
  const navigate = useNavigate();

  const [sub, setSub] = useState(null);
  const [busy, setBusy] = useState(null);
  const [checkoutUrl, setCheckoutUrl] = useState(null);
  const [couponCode, setCouponCode] = useState("");
  const [couponPreview, setCouponPreview] = useState(null);
  const { plans: dbPlans } = usePlans();

  // Build the list of plans to render. Prefer DB catalog; fall back to static defaults.
  const PLANS = dbPlans.length
    ? dbPlans.map((p) => ({
        id: p.plan_id,
        price_try: p.price_total,
        period_key: p.plan_id === "free"
          ? "billing.period_trial"
          : (p.days === 30 ? "billing.period_month"
             : p.days === 90 ? "billing.period_3month"
             : p.days === 180 ? "billing.period_6month"
             : "billing.period_month"),
        featured: p.featured,
        name: p.name_tr,
        name_en: p.name_en,
        features_tr: p.features_tr,
        features_en: p.features_en,
        days: p.days,
      }))
    : PLANS_STATIC_FALLBACK;

  const loadSub = async () => {
    try {
      const { data } = await api.get("/payments/subscription");
      setSub(data);
    } catch (e) {
      console.error("sub load failed", e);
    }
  };

  useEffect(() => {
    loadSub();
  }, []);

  // Detect ?payment=success redirect from iyzico callback
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("payment") === "success") {
      toast.success(t("billing.payment_success"));
      // Purchase conversion event to GA4 + Meta Pixel
      const purchasedPlan = params.get("plan");
      const planDoc = dbPlans.find((p) => p.plan_id === purchasedPlan);
      import("../components/app/AnalyticsPixels").then(({ trackPurchase }) => {
        trackPurchase({
          amount: planDoc?.price_total || 0,
          currency: "TRY",
          plan: purchasedPlan || "unknown",
        });
      });
      loadSub();
      if (refresh) refresh();
      navigate("/profile", { replace: true });
    } else if (params.get("payment") === "failed") {
      toast.error(t("billing.payment_failed"));
      navigate("/profile", { replace: true });
    }
  }, [location.search]);

  const upgrade = async (planId) => {
    if (planId === "free") return;
    setBusy(planId);
    try {
      const body = { plan: planId };
      if (couponCode.trim()) body.coupon_code = couponCode.trim().toUpperCase();
      const { data } = await api.post("/payments/checkout/initialize", body);
      if (data.free_activated) {
        // 100% coupon → plan activated instantly, no PayTR iframe.
        toast.success(t("billing.payment_success"));
        setCouponCode("");
        setCouponPreview(null);
        if (refresh) await refresh();
      } else {
        // PayTR: iframe_url is a full URL — render inside our modal iframe.
        setCheckoutUrl(data.iframe_url || "");
      }
    } catch (e) {
      const detail = e.response?.data?.detail || t("billing.init_failed");
      toast.error(detail);
    } finally {
      setBusy(null);
    }
  };

  const validateCoupon = async (planId = "monthly") => {
    if (!couponCode.trim()) { setCouponPreview(null); return; }
    try {
      const { data } = await api.post("/payments/coupon/validate", {
        code: couponCode.trim().toUpperCase(),
        plan: planId,
      });
      setCouponPreview(data);
      toast.success(t("billing.coupon_valid", { amount: data.discount_try.toFixed(2) }));
    } catch (e) {
      setCouponPreview(null);
      toast.error(t("billing.coupon_invalid"));
    }
  };

  const cancelAutoRenew = async () => {
    try {
      await api.post("/payments/subscription/cancel");
      toast.success(t("billing.cancel_success"));
      loadSub();
    } catch {
      toast.error(t("billing.cancel_failed"));
    }
  };

  const formatPrice = (planId) => {
    if (planId === "free") return lang === "tr" ? "Ücretsiz" : "Free";
    const p = PLANS.find((x) => x.id === planId);
    return `${p.price_try}₺ ${t(p.period_key)}`;
  };

  const expiryDate = sub?.plan_expires_at
    ? new Date(sub.plan_expires_at).toLocaleDateString(lang === "tr" ? "tr-TR" : "en-US", { year: "numeric", month: "long", day: "numeric" })
    : null;

  const features = (tier) => {
    if (tier.features_tr || tier.features_en) {
      return lang === "tr" ? (tier.features_tr || []) : (tier.features_en || []);
    }
    if (tier.id === "free") return [t("billing.feat_free_1"), t("billing.feat_free_2"), t("billing.feat_free_3")];
    return [
      t("billing.feat_full_1"), t("billing.feat_full_2"), t("billing.feat_full_3"),
      t("billing.feat_full_4"), t("billing.feat_full_5"), t("billing.feat_full_6"),
    ];
  };

  return (
    <AppShell>
      <div className="overline text-[#002FA7]">{t("profile.overline")}</div>
      <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-1">{t("profile.title")}</h1>

      <div className="mt-8 grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Account card */}
        <div className="card lg:col-span-1" data-testid="profile-card">
          <div className="overline">{t("profile.account")}</div>
          <div className="mt-4 space-y-3">
            <div>
              <div className="overline">{t("common.name")}</div>
              <div className="font-medium">{user?.name}</div>
            </div>
            <div>
              <div className="overline">{t("common.email")}</div>
              <div className="font-medium">{user?.email}</div>
            </div>
            <div>
              <div className="overline">{t("profile.signin_method")}</div>
              <div className="font-medium capitalize">{user?.auth_provider}</div>
            </div>
            <div className="pt-3 border-t border-slate-200">
              <div className="overline">{t("profile.plan")}</div>
              <div className="font-display text-xl font-bold capitalize" data-testid="current-plan">{sub?.plan || user?.plan || "free"}</div>
              {expiryDate && (
                <div className="text-xs text-slate-500 mt-1" data-testid="plan-expiry">
                  {t("billing.expires_on")}: <span className="font-medium text-slate-700">{expiryDate}</span>
                </div>
              )}
            </div>

            {sub?.card_last_four && (
              <div className="pt-3 border-t border-slate-200">
                <div className="overline flex items-center gap-1.5"><CreditCard size={12} weight="fill" /> {t("billing.saved_card")}</div>
                <div className="font-mono text-sm mt-1">{sub.card_brand || "CARD"} •••• {sub.card_last_four}</div>
              </div>
            )}

            {sub?.plan !== "free" && sub?.plan_expires_at && (
              <button onClick={cancelAutoRenew} className="btn-secondary !py-2 text-xs w-full mt-2" data-testid="cancel-auto-renew">
                {t("billing.cancel_renew")}
              </button>
            )}

            {!sub?.is_configured && (
              <div className="mt-3 p-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-xs" data-testid="paytr-not-configured">
                {t("billing.not_configured_warning")}
              </div>
            )}
          </div>
        </div>

        {/* Plans */}
        <div className="lg:col-span-2 space-y-4">
          {/* Coupon input */}
          <div className="card !p-4" data-testid="coupon-input-card">
            <div className="overline">{t("billing.have_coupon")}</div>
            <div className="mt-2 flex gap-2">
              <input
                value={couponCode}
                onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                placeholder="WELCOME20"
                className="flex-1 font-mono"
                data-testid="coupon-input"
              />
              <button onClick={() => validateCoupon("pro")} className="btn-secondary !py-2 text-sm" data-testid="coupon-apply-btn">
                {t("billing.apply_coupon")}
              </button>
            </div>
            {couponPreview && (
              <div className="mt-2 text-xs text-emerald-700" data-testid="coupon-preview">
                ✓ {couponPreview.coupon_code} — {couponPreview.discount_try.toFixed(2)}₺ {t("billing.discount_applied")}
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {PLANS.map((tier) => {
            const isCurrent = (sub?.plan || user?.plan) === tier.id;
            return (
              <div key={tier.id} className={`card card-hover relative ${tier.featured ? "border-t-4 border-t-[#002FA7] shadow-md" : ""}`} data-testid={`profile-tier-${tier.id}`}>
                {tier.featured && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#002FA7] text-white text-[10px] font-mono uppercase tracking-wider px-3 py-1 rounded-full">
                    {t("profile.recommended")}
                  </div>
                )}
                <div className="overline">{t(`billing.plan_${tier.id}_name`)}</div>
                <div className="font-display text-3xl font-bold mt-2">{formatPrice(tier.id)}</div>
                {tier.id !== "free" && (
                  <div className="text-xs text-slate-500 mt-1">{t("billing.kdv_included")}</div>
                )}
                <ul className="mt-4 space-y-1.5 text-sm">
                  {features(tier).map((f, k) => (
                    <li key={k} className="flex items-start gap-2">
                      <CheckCircle size={14} weight="fill" className="text-[#059669] mt-1 flex-shrink-0" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <button
                  disabled={isCurrent || busy === tier.id || tier.id === "free"}
                  onClick={() => upgrade(tier.id)}
                  className={`mt-5 w-full ${tier.featured ? "btn-primary" : "btn-secondary"} disabled:opacity-50 disabled:cursor-not-allowed`}
                  data-testid={`profile-upgrade-${tier.id}`}
                >
                  {isCurrent
                    ? t("profile.current")
                    : busy === tier.id
                    ? t("billing.starting")
                    : tier.id === "free"
                    ? t("billing.always_free")
                    : t("billing.upgrade_paytr")}
                </button>
                {tier.id !== "free" && (
                  <div className="mt-3 flex items-center justify-center gap-1.5 text-[10px] text-slate-500">
                    <ShieldCheck size={11} weight="fill" />
                    <span>{t("billing.paytr_safe")}</span>
                  </div>
                )}
              </div>
            );
          })}
          </div>
        </div>
      </div>

      {/* PayTR Checkout iframe Overlay */}
      {checkoutUrl && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" data-testid="checkout-overlay">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[92vh] overflow-hidden relative flex flex-col">
            <button
              onClick={() => setCheckoutUrl(null)}
              className="absolute top-3 right-3 z-10 w-8 h-8 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center"
              data-testid="checkout-close"
              aria-label="Close"
            >
              <X size={16} weight="bold" />
            </button>
            <div className="p-5 border-b border-slate-100">
              <div className="overline text-[#002FA7]">{t("billing.secure_payment")}</div>
              <h2 className="font-display text-xl font-bold mt-1">{t("billing.complete_payment")}</h2>
              <p className="text-xs text-slate-500 mt-1">{t("billing.paytr_safe")}</p>
            </div>
            <iframe
              src={checkoutUrl}
              title="PayTR ödeme"
              className="w-full flex-1 min-h-[600px] border-0"
              data-testid="paytr-iframe"
              allow="payment"
            />
          </div>
        </div>
      )}
    </AppShell>
  );
}
